import React from 'react';
import { useRouter } from '@tanstack/react-router';
import {
    Card,
    Space,
    Table,
    Tabs,
    Typography,
    Button,
    Tag,
    Spin,
    Alert,
} from 'antd';
import type { TabsProps } from 'antd';
import {
    CheckCircleOutlined,
    StopOutlined,
    SyncOutlined,
    PlusOutlined,
    EditOutlined,
} from "@ant-design/icons";
import { useAppQuery } from '@/common/hooks/useAppQuery';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { API_ENDPOINTS as API_MANAGEMENT_ENDPOINTS, QUERY_KEYS as QUERY_MANAGEMENT_KEYS } from '../../../management/api/apiEndpoints';
import { get, post } from '@/common/services/httpClient';

import type {
    CompanyType,
    InvoiceType,
    PolicyHolder,
    PolicyHolderDependent,
} from '../../types/participantsTypes';
import type { ApiResponse } from '../../../auth/types/authTypes';
import dayjs from 'dayjs';
import StatusTag from '@/common/components/Status';
import AmountFormat from '@/common/components/AmountFormat';
import type { ColumnsType } from 'antd/es/table';
import InvoicePrintButton from "../../pages/policyHolder/InvoicePrint";
import InvoiceEmail from '../../../../common/components/InvoiceEmail';
import Confirmation from '../../../../common/components/ConfirmButton';
import type { UserType } from '../../../management/types/managementTypes';
import { getPermissionKeyFromApi } from '../../../../common/components/fromatPermission';
import { hasPermission } from '../../../../common/services/permissionService';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

// ============== Fetch Detail ==============
const fetchDetail = async (Id: string): Promise<CompanyType> => {
    const response = await get<ApiResponse<CompanyType>>(
        `${API_ENDPOINTS.PARTICIPANTS.GET_COMPANY_BY_DETAIL}?Id=${Id}`,
    );
    return response.data;
};

// ============== Section Wrapper ==============
const Section = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <div
        style={{
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            padding: '24px',
            marginBottom: '24px',
            backgroundColor: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
    >
        <h3 style={{ marginBottom: 16 }}>{title}</h3>
        {children}
    </div>
);

// ============== User Fetch Function ==============
const fetchCompanyUsers = async (CompanyId: string): Promise<UserType[]> => {
    const payload = { CompanyId };
    const response = await post<ApiResponse<UserType[]>>(
        API_ENDPOINTS.PARTICIPANTS.GET_USERS,
        payload,
    );
    return response.data;
};

// ============== Main Component ==============
const Detail: React.FC = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const state = router?.state?.location?.state as { record?: CompanyType } | undefined;
    const record = state?.record;
    const Id = record?.id?.toString() || '';

    const { data: companyData, isLoading, isError } = useAppQuery(
        [QUERY_KEYS.PARTICIPANTS.COMPANY, Id],
        () => fetchDetail(Id),
        { enabled: !!Id },
    );

    // ============== Fetch Users for User Tab ==============
    const {
        data: userData,
        isLoading: isUsersLoading,
        isError: isUsersError,
    } = useAppQuery(
        [QUERY_KEYS.PARTICIPANTS.GET_COMPANY_DETAIL, Id],
        () => fetchCompanyUsers(Id),
        { enabled: !!Id },
    );

    const onChange = (key: string) => console.log('Selected Tab:', key);
    // ============== User Columns ==============
    const userColumns = (router: ReturnType<typeof useRouter>): ColumnsType<UserType> => [
        { title: t('Name'), dataIndex: 'name', key: 'name', width: 150 },
        { title: t('User Name'), dataIndex: 'userName', key: 'userName', width: 150 },
        { title: t('Phone No'), dataIndex: 'cellNo', key: 'cellNo', width: 150 },
        {
            title: t('Active'),
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Yes' : 'No'}
                </Tag>
            ),
        },
        {
            title: t("Actions"),
            key: "actions",
            width: 20,
            render: (_: any, record: UserType) => (
                <>
                    {!record.isActive && !record.isEmailActivated && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.RESEND_ACTIVATION_EMAIL_USER}
                            id={record.email}
                            title="Resend Email"
                            method="post"
                            icon={<SyncOutlined />}
                            payloadKey="email"
                            confirmButtonText={t(`Are you sure you want to resend activation email ?`)}
                            queryKey={QUERY_MANAGEMENT_KEYS.MANAGEMENT.USERS}
                            successMessage="Email sent successfully"
                        />
                    )}
                    {record.isEmailActivated && record.isActive && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.ACTIVATE_DEACTIVATE_USER}
                            id={record.id}
                            title="Deactivate"
                            method="post"
                            icon={<StopOutlined style={{ color: "red" }} />}
                            isPostById={true}
                            payloadKey="Id"
                            confirmButtonText="Are you sure you want to deactivate this user?"
                            queryKey={QUERY_MANAGEMENT_KEYS.MANAGEMENT.USERS}
                            successMessage="User deactivated successfully"
                        />
                    )}
                    {record.isEmailActivated && !record.isActive && (
                        <Confirmation
                            apiUrl={API_MANAGEMENT_ENDPOINTS.MANAGEMENT.ACTIVATE_DEACTIVATE_USER}
                            id={record.id}
                            title="Activate"
                            method="post"
                            icon={<CheckCircleOutlined style={{ color: "green" }} />}
                            isPostById={true}
                            payloadKey="Id"
                            confirmButtonText="Are you sure you want to activate this user?"
                            queryKey={QUERY_MANAGEMENT_KEYS.MANAGEMENT.USERS}
                            successMessage="User activated successfully"
                        />
                    )}
                    {
                        hasPermission(getPermissionKeyFromApi(`${API_MANAGEMENT_ENDPOINTS.MANAGEMENT.GET_USER_BY_ID}`)) && (
                            <Button
                                type="default"
                                icon={<EditOutlined style={{ color: '#1890ff' }} />}
                                title="Edit User"
                                style={{ marginLeft: 8, borderColor: '#1890ff' }}
                                onClick={async () => {
                                    try {
                                        const response = await get<ApiResponse<UserType>>(
                                            `${API_MANAGEMENT_ENDPOINTS.MANAGEMENT.GET_USER_BY_ID}/${record?.id}`
                                        );
                                        const userDetail = response.data;
                                        router.navigate({
                                            to: '/participants/company/detail/user',
                                            state: {
                                                companyId: record?.companyId,
                                                record: userDetail,
                                                companyname: companyData?.name,
                                            } as Record<string, unknown>,
                                        });
                                    } catch (error) {
                                        console.error("Error fetching user detail:", error);
                                    }
                                }}
                            />
                        )}
                </>
            ),
        },
    ];
    const items: TabsProps['items'] = [
        {
            key: '1',
            label: t('Policy Holder(s)'),
            children: (
                <Section title={t(`Policy Holder(s)`)}>
                    <Table<PolicyHolder>
                        columns={[
                            { title: t('Card Number'), dataIndex: 'cardNumber', key: 'cardNumber', fixed: 'right' },
                            { title: t('Name'), dataIndex: 'name', key: 'name' },
                            {
                                title: t('DOB'),
                                dataIndex: 'dob',
                                key: 'dob',
                                render: (text: string) => text ? dayjs(text).format('DD-MM-YYYY') : '',
                            },
                            { title: t('Category'), dataIndex: 'category', key: 'category' },
                            {
                                title: t('Amount'),
                                dataIndex: 'premiumAmount',
                                key: 'premiumAmount',
                                render: (value: number, record: any) => (
                                    <AmountFormat
                                        value={value ?? null}
                                        currencyCode={record.currencyISOCode ?? ''}
                                        amountInWord={false}
                                    />
                                ),
                            },
                            {
                                title: t('Status'),
                                dataIndex: 'status',
                                key: 'status',
                                render: (status: string) => <StatusTag status={status} />,
                            },
                        ]}
                        dataSource={companyData?.policyHolder || []}
                        rowKey="id"
                        expandable={{
                            expandedRowRender: (record: PolicyHolder) => (
                                <Table<PolicyHolderDependent>
                                    columns={[
                                        { title: t('Name'), dataIndex: 'name', key: 'name' },
                                        { title: t('Category'), dataIndex: 'category', key: 'category' },
                                        {
                                            title: t('Amount'),
                                            dataIndex: 'premiumAmount',
                                            key: 'premiumAmount',
                                            render: (value: number, record: any) => (
                                                <AmountFormat
                                                    value={value ?? null}
                                                    currencyCode={record.currencyISOCode ?? ''}
                                                    amountInWord={false}
                                                />
                                            ),
                                        },
                                    ]}
                                    dataSource={record.policyHolderDependent}
                                    rowKey="parentNationalIDNo"
                                    pagination={false}
                                    size="small"
                                />
                            ),
                            rowExpandable: (record) =>
                                record.policyHolderDependent?.length > 0,
                        }}
                        pagination={{

                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        //onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                        bordered
                    />
                </Section>
            ),
        },
        {
            key: '2',
            label: t('User'),
            children: (
                <Section title="">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <h3 style={{ margin: 0 }}>{t("User Management")}</h3>
                        {
                            hasPermission(getPermissionKeyFromApi(`${API_MANAGEMENT_ENDPOINTS.MANAGEMENT.SAVE_USER}`)) && (
                                <Button
                                    type="primary"
                                    onClick={() =>
                                        router.navigate({
                                            to: '/participants/company/detail/user',
                                            state: { companyId: record?.id, name: record?.name, companyname: record?.name } as Record<string, unknown>,
                                        })
                                    }
                                >
                                    <PlusOutlined /> {t("Add User")}
                                </Button>
                            )}
                    </div>

                    {isUsersLoading && <Spin size="large" />}
                    {isUsersError && <Alert type="error" message="Error fetching users" />}

                    {!isUsersLoading && !isUsersError && (
                        <Table<UserType>
                            columns={userColumns(router)}
                            dataSource={userData}
                            rowKey="id"

                            pagination={{
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} items`,
                            }}
                        />
                    )}
                </Section>
            ),
            forceRender: true,
        },
        {
            key: '3',
            label: t('Invoice'),
            children: (
                <Section title={t("Invoices")}>
                    <Table<InvoiceType>
                        columns={[
                            { title: t('Plan'), dataIndex: 'planName', key: 'planName' },
                            //{ title: t('Group'), dataIndex: 'groupName', key: 'groupName' },
                            {
                                title: t('Invoice Date'),
                                dataIndex: 'invoiceDate',
                                key: 'invoiceDate',
                                render: (text: string) => text ? dayjs(text).format('DD-MM-YYYY') : '',
                            },
                            {
                                title: t('Total Amount'),
                                dataIndex: 'totalAmount',
                                key: 'totalAmount',
                                render: (value: number, record: InvoiceType) =>
                                    `${value.toLocaleString()} ${record.currencyISOCode ?? ''}`,
                            },
                            {
                                title: t('Status'),
                                dataIndex: 'status',
                                key: 'status',
                                render: (status: string) => <StatusTag status={status} />,
                            },
                            {
                                title: t('Actions'),
                                key: 'actions',
                                render: (_: any, record: InvoiceType) => (
                                    <Space>
                                        <InvoicePrintButton invoice={record} />
                                        <InvoiceEmail invoice={record} />
                                    </Space>
                                ),
                            },
                        ]}
                        dataSource={companyData?.invoiceResponse}
                        rowKey="id"

                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} items`,
                        }}
                    />
                </Section>
            ),
            forceRender: true,
        },
    ];

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <Space size="small">
                            {companyData?.name || t('Company Detail')}
                            <StatusTag status={companyData?.planStatus} />
                        </Space>
                    </Title>
                    <Space size="middle" wrap>
                        <Text type="secondary">
                            {companyData?.address} {companyData?.country}
                        </Text>
                    </Space>
                    <Space size="middle" wrap>
                        <Text type="secondary">{companyData?.email},</Text>
                        <Text type="secondary">{companyData?.phoneNo}</Text>
                    </Space>
                </div>
            </Card>

            {isLoading && <Spin size="large" />}
            {isError && <Alert type="error" message="Error fetching data" />}

            {!isLoading && !isError && (
                <Tabs
                    defaultActiveKey="1"
                    items={items}
                    onChange={onChange}
                    destroyInactiveTabPane={false}
                />
            )}
        </div>
    );
};

export default Detail;
